export default function ImgPilotSupport() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Support - Image Compressor &amp; Resizer
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Help, troubleshooting, and contact details for Image Compressor
            &amp; Resizer.
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <h2 className="text-2xl font-bold text-[#A8720E]">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I compress a photo to an exact file size?
              </h3>
              <p className="mt-1">
                Tap <strong>Compress</strong> on the home screen, pick a
                photo, then set your target size in KB. The app compresses
                the image to hit that size and shows a before/after
                comparison before you save or share it.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I resize or crop a photo?
              </h3>
              <p className="mt-1">
                Tap <strong>Resize</strong> to set exact dimensions or a
                percentage, or tap <strong>Crop</strong> to trim freely or
                to a standard aspect ratio. <strong>Smart Crop</strong>{" "}
                applies common platform presets automatically.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How does background removal work?
              </h3>
              <p className="mt-1">
                Tap <strong>Remove BG</strong> (Pro), pick a photo, and the
                app removes the background entirely on your device — no
                cloud upload, no account needed. You get back a transparent
                PNG in seconds.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Does everything work offline?
              </h3>
              <p className="mt-1">
                Yes. Compression, resizing, cropping, converting,
                enhancing, rotating, EXIF stripping, and background removal
                all run entirely on your device. Network access is used
                only for showing ads and the rotating cross-promotion
                banner.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                What image formats are supported?
              </h3>
              <p className="mt-1">
                ImgPilot works with JPG and PNG, and can convert between
                the two. Background removal exports a PNG so it can keep
                transparency.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I save or share the result?
              </h3>
              <p className="mt-1">
                After processing, use <strong>Save</strong> to write the
                result to your device&apos;s photo library, or{" "}
                <strong>Share</strong> to send it directly to WhatsApp,
                Instagram, email, or any other app.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                What does the Pro upgrade unlock?
              </h3>
              <p className="mt-1">
                The one-time Pro upgrade ($4.99, no subscription) removes
                ads and unlocks batch processing (apply the same edit to
                10+ photos at once), on-device background removal, and
                watermarking.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Why does the app show ads?
              </h3>
              <p className="mt-1">
                Ads help support continued development. You can remove all
                ads with a one-time Pro upgrade from Settings, which also
                unlocks batch processing, background removal, and
                watermarking.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                The app is crashing. What do I do?
              </h3>
              <p className="mt-1">
                Try closing and reopening the app. Make sure you have the
                latest version from Google Play. If a specific photo
                crashes the app, email us and describe what you were doing
                when it happened.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Contact Support
          </h2>
          <p>
            For help, bug reports, or privacy-related questions, email{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            . We usually respond within 24-48 hours.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Report a Bug
          </h2>
          <p>
            If something is not working as expected, include these details
            in your email:
          </p>
          <ul className="list-disc space-y-1 pl-6">
            <li>Your device model and Android OS version</li>
            <li>Your app version (found in Settings &gt; Version)</li>
            <li>What you were trying to do</li>
            <li>The error or unexpected result</li>
            <li>Steps to reproduce the issue, if known</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Important Links
          </h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              Privacy Policy:{" "}
              <a
                href="/apps/imgpilot/privacy-policy"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/imgpilot/privacy-policy
              </a>
            </li>
            <li>
              Support:{" "}
              <a
                href="/apps/imgpilot/support"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/imgpilot/support
              </a>
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            App Information
          </h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              <strong>App Name:</strong> Image Compressor &amp; Resizer
            </li>
            <li>
              <strong>Package:</strong> com.cosmovex.imgpilot
            </li>
            <li>
              <strong>Platform:</strong> Android
            </li>
            <li>
              <strong>Developer:</strong> Cosmovex
            </li>
            <li>
              <strong>Contact:</strong>{" "}
              <a
                href="mailto:trionapps@gmail.com"
                className="text-[#0F6BBF] underline"
              >
                trionapps@gmail.com
              </a>
            </li>
          </ul>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-sm text-[#8A93A2]">
          &copy; 2026 Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
